import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon,
  CalendarIcon,
  StarIcon,
  ArrowLeftIcon,
  PencilIcon,
  TrophyIcon,
  MapPinIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { usersApi } from '../services/usersApi'
import EditMemberModal from '../components/EditMemberModal'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { formatPhoneNumberForDisplay } from '../utils/phoneUtils'

interface Member {
  userId: string
  email: string
  firstName: string
  lastName: string
  name: string
  phone?: string
  mobile?: string
  role: 'user' | 'admin'
  status: 'pending' | 'approved' | 'denied'
  isApproved: boolean
  joinedYear?: string
  createdAt: string
  updatedAt: string
  bio?: string
  avatar?: string
  emergencyContact?: {
    name: string
    phone: string
    relationship: string
  }
  attendanceYears?: string[]
  attendanceHistory?: {
    year: string
    eventsAttended: number
    totalEvents: number
  }[]
  achievements?: string[]
}

export default function MemberDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth0()
  const [member, setMember] = useState<Member | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    loadMember(userId)
  }, [userId])

  const loadMember = async (id: string) => {
    try {
      setLoading(true)
      const userData = await usersApi.getUser(id)
      
      // Convert API user to Member format
      const memberData: Member = {
        userId: userData.userId,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        name: userData.name || `${userData.firstName} ${userData.lastName}`,
        phone: userData.mobile, // Using mobile as phone since that's what we store
        mobile: userData.mobile,
        role: userData.role || 'user',
        status: userData.status || 'approved',
        isApproved: userData.status === 'active' || userData.isActive,
        joinedYear: userData.joinedYear,
        createdAt: userData.createdAt || new Date().toISOString(),
        updatedAt: userData.updatedAt || new Date().toISOString(),
        emergencyContact: userData.emergencyContact,
        attendanceYears: userData.attendanceYears || [],
        // Default values for fields not in API
        bio: userData.bio || 'No bio available',
        avatar: userData.avatar,
        attendanceHistory: [],
        achievements: []
      }
      
      setMember(memberData)
    } catch (error) {
      toast.error('Failed to load member details')
    } finally {
      setLoading(false)
    }
  }


  const canEdit = user?.role === 'admin' || user?.userId === member?.userId
  const isOwnProfile = user?.userId === member?.userId

  const handleSaveMember = async (updatedMember: Member) => {
    try {
      await usersApi.updateUser(updatedMember.userId, {
        firstName: updatedMember.firstName,
        lastName: updatedMember.lastName,
        phone: updatedMember.phone,
        role: updatedMember.role,
        isActive: updatedMember.status === 'approved'
      })
      
      setMember(updatedMember)
      toast.success('Member updated successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update member')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!member) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Member not found</h3>
        <button
          onClick={() => navigate('/members')}
          className="mt-4 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          ← Back to Members
        </button>
      </div>
    )
  }

  const totalAttendance = member.attendanceHistory?.reduce((sum, year) => sum + year.eventsAttended, 0) || 0
  const totalEvents = member.attendanceHistory?.reduce((sum, year) => sum + year.totalEvents, 0) || 0
  const attendanceRate = totalEvents > 0 ? Math.round((totalAttendance / totalEvents) * 100) : 0

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/members')}
            className="flex items-center space-x-2 text-white hover:text-gray-200 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Members</span>
          </button>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/legacy/gallery?members=${encodeURIComponent(member.name)}`)}
            className="flex items-center space-x-2 px-4 py-2 border border-white/30 rounded-md text-sm font-medium text-white hover:bg-white/10 transition-colors"
          >
            <LinkIcon className="w-4 h-4" />
            <span>View Photos</span>
          </button>
          {canEdit && (
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-white/30 rounded-md text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              <PencilIcon className="w-4 h-4" />
              <span>{isOwnProfile ? 'Edit Profile' : 'Edit Member'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="p-6">
          {/* Profile Header */}
          <div className="flex items-start space-x-6 mb-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center overflow-hidden">
                {member.avatar ? (
                  <img 
                    src={member.avatar} 
                    alt={`${member.name} avatar`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-primary-600 dark:text-primary-400 text-3xl font-semibold">
                    {member.firstName.charAt(0).toUpperCase()}{member.lastName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {member.name}
              </h1>
              <div className="flex items-center space-x-3 mt-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  member.role === 'admin' 
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                }`}>
                  {member.role === 'admin' ? 'Administrator' : 'Member'}
                </span>
                {member.joinedYear && (
                  <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center space-x-1">
                    <StarIcon className="w-4 h-4" />
                    <span>Member since {member.joinedYear}</span>
                  </span>
                )}
              </div>
              {member.bio && (
                <p className="mt-4 text-gray-600 dark:text-gray-300 leading-relaxed">
                  {member.bio}
                </p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{member.email}</span>
                </div>
                {member.phone && (
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-900 dark:text-white">{formatPhoneNumberForDisplay(member.phone)}</span>
                  </div>
                )}
              </div>
            </div>

            {member.emergencyContact && (user?.role === 'admin' || isOwnProfile) && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Emergency Contact</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{member.emergencyContact.name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Phone:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{formatPhoneNumberForDisplay(member.emergencyContact.phone)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Relationship:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{member.emergencyContact.relationship}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Member Stats */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Member Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {member.joinedYear ? new Date().getFullYear() - parseInt(member.joinedYear) + 1 : 'N/A'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Years as Member</div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">Events Attended</div>
                <div className="flex flex-wrap gap-2">
                  {member.attendanceYears?.sort((a, b) => parseInt(b) - parseInt(a)).map((year) => (
                    <span key={year} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                      {year}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      {member.achievements && member.achievements.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors duration-300">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <TrophyIcon className="w-5 h-5 text-yellow-500" />
              <span>Achievements</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {member.achievements.map((achievement, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800"
                >
                  <TrophyIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-yellow-800 dark:text-yellow-200 font-medium">
                    {achievement}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Attendance History */}
      {member.attendanceHistory && member.attendanceHistory.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors duration-300">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <span>Attendance History</span>
            </h3>
            <div className="space-y-3">
              {member.attendanceHistory.map((yearData) => {
                const rate = Math.round((yearData.eventsAttended / yearData.totalEvents) * 100)
                return (
                  <div key={yearData.year} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium text-gray-900 dark:text-white">{yearData.year}</span>
                      <span className="text-gray-600 dark:text-gray-300">
                        {yearData.eventsAttended} / {yearData.totalEvents} events
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white w-10">
                        {rate}%
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      <EditMemberModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        member={member}
        onSave={handleSaveMember}
        isOwnProfile={isOwnProfile}
      />
      </motion.div>
    </div>
  )
}
